/**
 * NOS-214: apply a guided-review decision choice through the existing
 * builder/customization write paths (page_sections.content, blog_posts
 * publish state, org_customizations nav CTA) — no new mutation surface.
 *
 * Planners are pure (unit-tested); `applyImportDecisionChoice` fetches the
 * org's sections, runs the planner for the chosen option, and persists the
 * planned writes with the same statements the customization store uses.
 */
import { supabase } from "@/lib/supabase"
import type { ImportDecision } from "@/hooks/useImportJob"
import type { Json } from "@/types/database"

/** Minimal section row the planners operate on (mirrors useImportReviewSections). */
export interface DecisionSectionRow {
  id: string
  page_id: string
  section_type: string
  order_index: number
  content: Record<string, unknown>
  is_visible: boolean
}

export interface PlannedSectionUpdate {
  id: string
  content: Record<string, unknown>
}

/**
 * Full-bleed vs boxed backgrounds: operates on sections carrying a colored
 * `_styles.background`. Boxed adds `width: "contained"` (the builder's own
 * boxed semantic, NOS-154) + rounded corners; full-bleed removes them.
 * Sections already in the requested state produce no write.
 */
export function planBackgroundLayout(
  sections: DecisionSectionRow[],
  mode: "full_bleed" | "boxed"
): PlannedSectionUpdate[] {
  const updates: PlannedSectionUpdate[] = []
  for (const s of sections) {
    const styles = s.content?._styles as Record<string, unknown> | undefined
    const bg = styles?.background
    if (typeof bg !== "string" || bg === "" || bg === "transparent") continue
    const isBoxed = styles?.width === "contained"
    if (mode === "boxed" && !isBoxed) {
      updates.push({
        id: s.id,
        content: {
          ...s.content,
          _styles: { ...styles, width: "contained", corner_radius: styles?.corner_radius ?? "lg" },
        },
      })
    } else if (mode === "full_bleed" && isBoxed) {
      const next = { ...styles }
      delete next.width
      updates.push({ id: s.id, content: { ...s.content, _styles: next } })
    }
  }
  return updates
}

/**
 * image_text side placement: per page, image_text sections that render an
 * image get alternating sides in section order ("alternate") or all-left
 * ("flat" — the imported layout). Unchanged sections produce no write.
 */
export function planImageTextSides(
  sections: DecisionSectionRow[],
  mode: "alternate" | "flat"
): PlannedSectionUpdate[] {
  const byPage = new Map<string, DecisionSectionRow[]>()
  for (const s of sections) {
    if (s.section_type !== "image_text") continue
    const img = s.content?.image_url
    if (typeof img !== "string" || img.trim() === "") continue
    const list = byPage.get(s.page_id) ?? []
    list.push(s)
    byPage.set(s.page_id, list)
  }
  const updates: PlannedSectionUpdate[] = []
  for (const list of byPage.values()) {
    // Alternation needs rhythm — a lone image_text on a page stays put (in
    // both modes, so "flat" never overrides a deliberate side on a lone block).
    if (list.length < 2) continue
    const ordered = [...list].sort((a, b) => a.order_index - b.order_index)
    ordered.forEach((s, i) => {
      const want = mode === "alternate" ? (i % 2 === 0 ? "left" : "right") : "left"
      const current = (s.content?.image_position as string | undefined) ?? "left"
      if (current === want) return
      updates.push({ id: s.id, content: { ...s.content, image_position: want } })
    })
  }
  return updates
}

/**
 * NOS-238: white-logo dark treatment. Targets logo_grid sections the import
 * flagged (any logo entry with `light: true`) or that already carry an
 * explicit `logo_treatment`. "dark" restores the recommended treatment,
 * "original" shows source colors (the renderer's auto rule makes flagged rows
 * dark when `logo_treatment` is absent, so both modes write explicit values).
 * Sections already in the requested effective state produce no write.
 */
export function planLogoGridTreatment(
  sections: DecisionSectionRow[],
  mode: "dark" | "original"
): PlannedSectionUpdate[] {
  const updates: PlannedSectionUpdate[] = []
  for (const s of sections) {
    if (s.section_type !== "logo_grid") continue
    const logos = Array.isArray(s.content?.logos)
      ? (s.content.logos as Array<{ light?: unknown }>)
      : []
    const hasLight = logos.some((l) => l?.light === true)
    const current = s.content?.logo_treatment
    const explicit = current === "dark" || current === "original" ? current : null
    // Untouched by the import and never overridden — not a decision target.
    if (!hasLight && !explicit) continue
    const effective = explicit ?? (hasLight ? "dark" : "default")
    if (effective === mode) continue
    updates.push({ id: s.id, content: { ...s.content, logo_treatment: mode } })
  }
  return updates
}

/**
 * Synthesized homepage hero: find the hero the import synthesized on the
 * given page (provenance `_synthesized: true`) — hiding uses the builder's
 * own is_visible toggle, so the section survives for later re-enable.
 */
export function findSynthesizedHero(
  sections: DecisionSectionRow[],
  pageId: string | null
): DecisionSectionRow | null {
  if (!pageId) return null
  return (
    sections.find(
      (s) =>
        s.page_id === pageId &&
        s.section_type === "hero" &&
        s.content?._synthesized === true
    ) ?? null
  )
}

async function fetchOrgSections(orgId: string): Promise<DecisionSectionRow[]> {
  const { data, error } = await supabase
    .from("page_sections")
    .select("id, page_id, section_type, order_index, content, is_visible")
    .eq("org_id", orgId)
    .order("order_index", { ascending: true })
  if (error) throw new Error(error.message)
  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>
    return {
      id: r.id as string,
      page_id: r.page_id as string,
      section_type: r.section_type as string,
      order_index: (r.order_index as number) ?? 0,
      content:
        r.content != null && typeof r.content === "object" && !Array.isArray(r.content)
          ? (r.content as Record<string, unknown>)
          : {},
      is_visible: r.is_visible !== false,
    }
  })
}

/** Same persistence statement the store's persistSectionContent uses. */
async function persistSectionUpdates(updates: PlannedSectionUpdate[]): Promise<void> {
  for (const u of updates) {
    const { error } = await supabase
      .from("page_sections")
      .update({ content: u.content as Json })
      .eq("id", u.id)
    if (error) throw new Error(error.message)
  }
}

export interface ApplyDecisionContext {
  orgId: string
  /** The org's current nav CTA label ("" = hidden). */
  currentNavCtaText: string
  /** Store action — the builder's own nav CTA write path. */
  updateNavCtaText: (text: string) => Promise<void>
  /** Slugs of the posts this import created (importedPostSlugsFromJob). */
  importedPostSlugs: string[]
  /** NOS-239: the org's current announcement config (jsonb, may be null). */
  currentAnnouncement?: Record<string, unknown> | null
  /** NOS-239: store action — the builder's own announcement write path. */
  updateAnnouncement?: (config: Record<string, unknown> | null) => Promise<void>
}

export interface ApplyDecisionResult {
  /** Rows/sections actually written (0 = the choice already matched). */
  changed: number
}

const POST_UPDATE_CHUNK = 100

/**
 * Apply one chosen option. Recommended defaults generally match what the
 * import already did, so accepting them writes nothing — choosing the other
 * side performs the (reversible-by-normal-editing) writes.
 */
export async function applyImportDecisionChoice(
  decision: ImportDecision,
  optionId: string,
  ctx: ApplyDecisionContext
): Promise<ApplyDecisionResult> {
  switch (decision.key) {
    case "donate_nav_cta": {
      if (optionId === "remove") {
        if (ctx.currentNavCtaText.trim() === "") return { changed: 0 }
        await ctx.updateNavCtaText("")
        return { changed: 1 }
      }
      // keep — restore the default label if a previous choice hid it
      if (ctx.currentNavCtaText.trim() === "") {
        await ctx.updateNavCtaText("Donate")
        return { changed: 1 }
      }
      return { changed: 0 }
    }

    case "posts_published": {
      const publish = optionId !== "unpublish"
      if (publish) return { changed: 0 } // imported posts are already live
      const slugs = ctx.importedPostSlugs
      if (slugs.length === 0) return { changed: 0 }
      for (let i = 0; i < slugs.length; i += POST_UPDATE_CHUNK) {
        const chunk = slugs.slice(i, i + POST_UPDATE_CHUNK)
        const { error } = await supabase
          .from("blog_posts")
          .update({ is_published: false, updated_at: new Date().toISOString() })
          .eq("org_id", ctx.orgId)
          .in("slug", chunk)
        if (error) throw new Error(error.message)
      }
      return { changed: slugs.length }
    }

    case "hero_synthesized": {
      const slug = (decision.payload?.slug as string | undefined) ?? "home"
      const { data: pageRow, error: pageErr } = await supabase
        .from("pages")
        .select("id")
        .eq("org_id", ctx.orgId)
        .eq("slug", slug)
        .maybeSingle()
      if (pageErr) throw new Error(pageErr.message)
      const sections = await fetchOrgSections(ctx.orgId)
      const hero = findSynthesizedHero(sections, (pageRow?.id as string | undefined) ?? null)
      if (!hero) return { changed: 0 }
      const wantVisible = optionId !== "hide_hero"
      if (hero.is_visible === wantVisible) return { changed: 0 }
      const { error } = await supabase
        .from("page_sections")
        .update({ is_visible: wantVisible })
        .eq("id", hero.id)
      if (error) throw new Error(error.message)
      return { changed: 1 }
    }

    case "fullbleed_backgrounds": {
      const sections = await fetchOrgSections(ctx.orgId)
      const updates = planBackgroundLayout(
        sections,
        optionId === "boxed" ? "boxed" : "full_bleed"
      )
      await persistSectionUpdates(updates)
      return { changed: updates.length }
    }

    case "image_text_sides": {
      // NOS-236: when the import detected sides from the source layout
      // (payload.detected), "keep" must NOT flatten them back to all-left —
      // the imported state IS the source state, so keeping writes nothing.
      if (optionId !== "alternate" && decision.payload?.detected === true) {
        return { changed: 0 }
      }
      const sections = await fetchOrgSections(ctx.orgId)
      const updates = planImageTextSides(
        sections,
        optionId === "alternate" ? "alternate" : "flat"
      )
      await persistSectionUpdates(updates)
      return { changed: updates.length }
    }

    case "announcement_bar": {
      // NOS-239: the import wrote the config DISABLED — "enable" flips it on
      // through the builder's own update path; "keep_off" changes nothing.
      if (optionId !== "enable") return { changed: 0 }
      if (!ctx.updateAnnouncement) return { changed: 0 }
      const current =
        ctx.currentAnnouncement != null &&
        typeof ctx.currentAnnouncement === "object" &&
        !Array.isArray(ctx.currentAnnouncement)
          ? ctx.currentAnnouncement
          : null
      if (current?.enabled === true) return { changed: 0 }
      // The stored config is the source of truth; the card payload rebuilds it
      // if mapping wrote nothing (defensive — older imports, partial failures).
      const base: Record<string, unknown> =
        current ??
        ({
          text: decision.payload?.text ?? "",
          ...(decision.payload?.link_label ? { link_label: decision.payload.link_label } : {}),
          ...(decision.payload?.link_href ? { link_href: decision.payload.link_href } : {}),
          dismissible: true,
          source: "import",
        } as Record<string, unknown>)
      if (typeof base.text !== "string" || base.text === "") return { changed: 0 }
      await ctx.updateAnnouncement({ ...base, enabled: true })
      return { changed: 1 }
    }

    case "light_logos": {
      const sections = await fetchOrgSections(ctx.orgId)
      const updates = planLogoGridTreatment(
        sections,
        optionId === "original_colors" ? "original" : "dark"
      )
      await persistSectionUpdates(updates)
      return { changed: updates.length }
    }

    default:
      // Unknown decision from a newer schema — accept silently, change nothing.
      return { changed: 0 }
  }
}
